// Per-project status management section — rendered inside ProjectSettingsPanel.
// Features: list (sortOrder), add, inline-edit (name/category/color), reorder
// with up/down, delete with confirm. 409 (status in use) surfaces a toast
// instead of crashing.

import { useState } from "preact/hooks";
import { ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-preact";
import type { Status, StatusCategory } from "@juuplan/shared";
import { STATUS_CATEGORIES } from "@juuplan/shared";
import { COLOR_PRESETS } from "../lib/format";
import { toastError, toastSuccess } from "../lib/toast";
import {
  useStatuses,
  addStatus,
  patchStatus,
  reorderStatuses,
  removeStatus,
} from "../store/statuses";
import { ApiError } from "../lib/api";

import { Button } from "./ui/Button";

const CATEGORY_LABEL: Record<StatusCategory, string> = {
  backlog: "Backlog",
  unstarted: "Nicht gestartet",
  started: "Gestartet",
  review: "Review",
  done: "Erledigt",
  cancelled: "Abgebrochen",
};

interface Props {
  projectId: number;
  /** When false the mutating controls are hidden (view-only role). */
  canEdit: boolean;
}

interface EditState {
  name: string;
  category: StatusCategory;
  color: string;
  /** Kanban WIP-Limit; null = kein Limit. */
  wipLimit: number | null;
}

/**
 * Per-project status management list.
 * Renders inside ProjectSettingsPanel as an accordion-style section.
 */
export function StatusesSection(props: Props): preact.JSX.Element {
  const { projectId, canEdit } = props;
  const { items, loading, error } = useStatuses(projectId);

  // Row currently being edited (id -> draft)
  const [editing, setEditing] = useState<Map<number, EditState>>(new Map());
  // Rows pending a save
  const [saving, setSaving] = useState<Set<number>>(new Set());
  // Rows pending a delete confirm
  const [confirmDel, setConfirmDel] = useState<Set<number>>(new Set());

  // New-status form state
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [newCategory, setNewCategory] = useState<StatusCategory>("unstarted");
  const [newColor, setNewColor] = useState<string>(COLOR_PRESETS[0]);
  const [creating, setCreating] = useState(false);

  // ---- helpers ----

  const startEdit = (s: Status): void => {
    setEditing((prev) => {
      const next = new Map(prev);
      next.set(s.id, { name: s.name, category: s.category, color: s.color, wipLimit: s.wip_limit });
      return next;
    });
  };

  const cancelEdit = (id: number): void => {
    setEditing((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  };

  const setDraft = (id: number, patch: Partial<EditState>): void => {
    setEditing((prev) => {
      const cur = prev.get(id);
      if (!cur) return prev;
      const next = new Map(prev);
      next.set(id, { ...cur, ...patch });
      return next;
    });
  };

  const commitEdit = async (s: Status): Promise<void> => {
    const draft = editing.get(s.id);
    if (!draft) return;
    setSaving((prev) => new Set(prev).add(s.id));
    try {
      await patchStatus(s.id, projectId, {
        name: draft.name.trim() || s.name,
        category: draft.category,
        color: draft.color,
        wip_limit: draft.wipLimit,
      });
      cancelEdit(s.id);
      toastSuccess("Status gespeichert.");
    } catch {
      // api wrapper already toasted
    } finally {
      setSaving((prev) => {
        const next = new Set(prev);
        next.delete(s.id);
        return next;
      });
    }
  };

  const handleDelete = async (s: Status): Promise<void> => {
    setSaving((prev) => new Set(prev).add(s.id));
    try {
      await removeStatus(s.id, projectId);
      setConfirmDel((prev) => {
        const next = new Set(prev);
        next.delete(s.id);
        return next;
      });
      toastSuccess("Status gelöscht.");
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        toastError("Status wird von Aufgaben verwendet und kann nicht gelöscht werden.");
      }
      // other errors already toasted by the api wrapper
    } finally {
      setSaving((prev) => {
        const next = new Set(prev);
        next.delete(s.id);
        return next;
      });
    }
  };

  const moveUp = async (idx: number): Promise<void> => {
    if (idx === 0) return;
    const next = [...items];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    await reorderStatuses(projectId, next.map((s) => s.id));
  };

  const moveDown = async (idx: number): Promise<void> => {
    if (idx >= items.length - 1) return;
    const next = [...items];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    await reorderStatuses(projectId, next.map((s) => s.id));
  };

  const submitNew = async (): Promise<void> => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      await addStatus(projectId, { name, category: newCategory, color: newColor });
      setNewName("");
      setNewCategory("unstarted");
      setNewColor(COLOR_PRESETS[0]);
      setShowNew(false);
      toastSuccess("Status angelegt.");
    } catch {
      // api wrapper already toasted
    } finally {
      setCreating(false);
    }
  };

  // ---- render ----

  return (
    <div class="flex flex-col gap-2">
      <div class="text-label font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)]">
        Status-Definitionen
      </div>

      {loading && (
        <p class="text-xs text-[color:var(--color-on-surface-variant)]">Lädt…</p>
      )}

      {error && (
        <p class="text-xs text-[color:var(--color-error)]">
          Fehler: {error}
        </p>
      )}

      {!loading && !error && items.length === 0 && (
        <p class="text-xs text-[color:var(--color-on-surface-variant)] italic">
          Noch keine Status-Definitionen.
        </p>
      )}

      {!loading && items.map((s, idx) => {
        const draft = editing.get(s.id);
        const isSaving = saving.has(s.id);
        const isConfirmDel = confirmDel.has(s.id);

        return (
          <div
            key={s.id}
            class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] overflow-hidden"
          >
            {/* Row header */}
            <div class="flex items-center gap-2 px-2 py-1.5">
              {/* Color swatch */}
              {draft ? (
                <span
                  class="inline-block size-4 rounded-full shrink-0 border border-[color:var(--color-outline-variant)]"
                  style={{ background: draft.color }}
                  aria-hidden="true"
                />
              ) : (
                <span
                  class="inline-block size-4 rounded-full shrink-0"
                  style={{ background: s.color }}
                  aria-hidden="true"
                />
              )}

              {/* Name */}
              <span class="flex-1 text-xs font-medium text-[color:var(--color-on-surface)] truncate">
                {draft ? draft.name || s.name : s.name}
              </span>

              {/* Category badge */}
              <span class="shrink-0 text-label text-[color:var(--color-on-surface-variant)] bg-[color:var(--color-surface-container-low)] rounded px-1.5 py-0.5">
                {CATEGORY_LABEL[draft ? draft.category : s.category]}
              </span>

              {/* WIP-Limit badge */}
              {(draft ? draft.wipLimit : s.wip_limit) !== null && (
                <span
                  class="shrink-0 text-label font-semibold text-[color:var(--color-primary)] bg-[color:var(--color-primary-container,#DBEAFE)] rounded px-1.5 py-0.5"
                  title="Kanban WIP-Limit"
                >
                  WIP {draft ? draft.wipLimit : s.wip_limit}
                </span>
              )}

              {/* Reorder + actions (edit role only) */}
              {canEdit && !draft && (
                <div class="flex items-center gap-0.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => void moveUp(idx)}
                    disabled={idx === 0 || isSaving}
                    class="rounded p-0.5 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] disabled:opacity-30 focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--color-primary)]"
                    aria-label="Nach oben"
                  >
                    <ChevronUp size={14} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={() => void moveDown(idx)}
                    disabled={idx >= items.length - 1 || isSaving}
                    class="rounded p-0.5 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] disabled:opacity-30 focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--color-primary)]"
                    aria-label="Nach unten"
                  >
                    <ChevronDown size={14} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={() => startEdit(s)}
                    class="rounded px-1.5 py-0.5 text-label text-[color:var(--color-primary)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--color-primary)]"
                  >
                    Bearbeiten
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setConfirmDel((prev) => {
                        const next = new Set(prev);
                        next.add(s.id);
                        return next;
                      })
                    }
                    disabled={isSaving}
                    class="rounded p-0.5 text-[color:var(--color-error)] hover:bg-[color:var(--color-error-container)] disabled:opacity-30 focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--color-error)]"
                    aria-label="Status löschen"
                  >
                    <Trash2 size={14} aria-hidden="true" />
                  </button>
                </div>
              )}
            </div>

            {/* Inline editor */}
            {draft && canEdit && (
              <div class="border-t border-[color:var(--color-outline-variant)] px-2 pb-2 pt-2 flex flex-col gap-2">
                <input
                  type="text"
                  value={draft.name}
                  maxLength={100}
                  onInput={(e) =>
                    setDraft(s.id, { name: (e.target as HTMLInputElement).value })
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); void commitEdit(s); }
                    if (e.key === "Escape") cancelEdit(s.id);
                  }}
                  placeholder="Name"
                  class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
                  aria-label="Name"
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                />
                <select
                  value={draft.category}
                  onChange={(e) =>
                    setDraft(s.id, {
                      category: (e.target as HTMLSelectElement).value as StatusCategory,
                    })
                  }
                  class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
                  aria-label="Kategorie"
                >
                  {STATUS_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {CATEGORY_LABEL[cat]}
                    </option>
                  ))}
                </select>
                <label class="flex items-center gap-2 text-micro text-[color:var(--color-on-surface-variant)]">
                  <span class="shrink-0">WIP-Limit (Kanban)</span>
                  <input
                    type="number"
                    min={1}
                    max={999}
                    value={draft.wipLimit ?? ""}
                    onInput={(e) => {
                      const raw = (e.target as HTMLInputElement).value.trim();
                      const n = raw === "" ? null : Number.parseInt(raw, 10);
                      setDraft(s.id, {
                        wipLimit: n !== null && Number.isFinite(n) && n > 0 ? n : null,
                      });
                    }}
                    placeholder="kein Limit"
                    class="w-24 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
                    aria-label="WIP-Limit"
                  />
                </label>
                <div class="flex flex-wrap gap-1" role="group" aria-label="Farbe wählen">
                  {COLOR_PRESETS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setDraft(s.id, { color: c })}
                      class={`size-5 rounded-full border-2 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] ${
                        draft.color === c
                          ? "border-[color:var(--color-on-surface)] scale-110"
                          : "border-transparent hover:scale-110"
                      }`}
                      style={{ background: c }}
                      aria-label={`Farbe ${c}`}
                      aria-pressed={draft.color === c}
                    />
                  ))}
                </div>
                <div class="flex items-center gap-1.5 justify-end">
                  <button
                    type="button"
                    onClick={() => cancelEdit(s.id)}
                    class="rounded border border-[color:var(--color-outline-variant)] px-2.5 py-1 text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors"
                  >
                    Abbrechen
                  </button>
                  <Button
                    variant="primary"
                    size="xs"
                    type="button"
                    onClick={() => void commitEdit(s)}
                    disabled={isSaving || !draft.name.trim()}
                  >
                    {isSaving ? "Speichere…" : "Speichern"}
                  </Button>
                </div>
              </div>
            )}

            {/* Delete confirm */}
            {isConfirmDel && !draft && canEdit && (
              <div class="border-t border-[color:var(--color-outline-variant)] px-2 py-2 flex flex-col gap-1.5">
                <p class="text-label text-[color:var(--color-on-error-container)] bg-[color:var(--color-error-container)] rounded px-2 py-1.5">
                  Status „{s.name}" wirklich löschen?
                </p>
                <div class="flex items-center gap-1.5 justify-end">
                  <button
                    type="button"
                    onClick={() =>
                      setConfirmDel((prev) => {
                        const next = new Set(prev);
                        next.delete(s.id);
                        return next;
                      })
                    }
                    class="rounded border border-[color:var(--color-outline-variant)] px-2.5 py-1 text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors"
                  >
                    Abbrechen
                  </button>
                  <Button
                    variant="destructive"
                    size="xs"
                    type="button"
                    onClick={() => void handleDelete(s)}
                    disabled={isSaving}
                  >
                    {isSaving ? "Lösche…" : "Löschen"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Add new status */}
      {canEdit && (
        <div class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] overflow-hidden">
          {!showNew ? (
            <button
              type="button"
              onClick={() => setShowNew(true)}
              class="w-full inline-flex items-center gap-1.5 px-2 py-1.5 text-xs text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--color-primary)] transition-colors"
            >
              <Plus size={14} aria-hidden="true" />
              Neuen Status anlegen
            </button>
          ) : (
            <div class="px-2 py-2 flex flex-col gap-2">
              <div class="text-label font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)]">
                Neuer Status
              </div>
              <input
                type="text"
                value={newName}
                maxLength={100}
                onInput={(e) =>
                  setNewName((e.target as HTMLInputElement).value)
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); void submitNew(); }
                  if (e.key === "Escape") {
                    setShowNew(false);
                    setNewName("");
                  }
                }}
                placeholder="Name des Status"
                class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
                aria-label="Name des neuen Status"
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
              />
              <select
                value={newCategory}
                onChange={(e) =>
                  setNewCategory(
                    (e.target as HTMLSelectElement).value as StatusCategory
                  )
                }
                class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
                aria-label="Kategorie"
              >
                {STATUS_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {CATEGORY_LABEL[cat]}
                  </option>
                ))}
              </select>
              <div class="flex flex-wrap gap-1" role="group" aria-label="Farbe wählen">
                {COLOR_PRESETS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setNewColor(c)}
                    class={`size-5 rounded-full border-2 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] ${
                      newColor === c
                        ? "border-[color:var(--color-on-surface)] scale-110"
                        : "border-transparent hover:scale-110"
                    }`}
                    style={{ background: c }}
                    aria-label={`Farbe ${c}`}
                    aria-pressed={newColor === c}
                  />
                ))}
              </div>
              <div class="flex items-center gap-1.5 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowNew(false);
                    setNewName("");
                  }}
                  class="rounded border border-[color:var(--color-outline-variant)] px-2.5 py-1 text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors"
                >
                  Abbrechen
                </button>
                <Button
                  variant="primary"
                  size="xs"
                  type="button"
                  onClick={() => void submitNew()}
                  disabled={!newName.trim() || creating}
                >
                  {creating ? "Lege an…" : "Erstellen"}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
